import * as metrics from '@fullstory/proto/fs/api/metrics';
import * as segment from '@fullstory/proto/fs/api/segment';
import {
  CustomFieldBoolOperator,
  CustomFieldBoolsOperator,
  CustomFieldDateOperator,
  CustomFieldDatesOperator,
  CustomFieldFloatOperator,
  CustomFieldFloatsOperator,
  CustomFieldIntOperator,
  CustomFieldIntsOperator,
  CustomFieldStringOperator,
  CustomFieldStringsOperator,
} from '@fullstory/proto/fs/api/segment';
import * as schema from '@fullstory/client-api/goimports/fs_shared_schema';
import { CustomField, FieldScope, parseUnscopedFieldName, VarType } from '@fullstory/search-vars';
import { assertExhaustive, isNonNull } from '@fullstory/typeutils';
import { int64 } from '@fullstory/proto/google/protobuf';

type BareDimension = Pick<
  metrics.Dimension,
  | 'builtInProperty'
  | 'customEventProperty'
  | 'userVarProperty'
  | 'pageProperty'
  | 'elementProperty'
  | 'prefix'
>;

export const dimensionsToDependentEventFilters = (
  dimensions: BareDimension[],
  values: string[],
  fallback?: boolean,
  // in the translation to the searchUI, not all values translate perfectly - set to true if you want it to be represented
  // in the search-ui
  forSearch?: boolean,
): segment.DependentEventFilter[] => {
  // todo(kevin): dimensions can have varied fallback values, change structure to account for this
  if (dimensions.length != values.length) {
    console.error(
      `length mismatch between dimensions and values. dimensions: ${JSON.stringify(
        dimensions,
      )}. values: ${values}`,
    );
    return [];
  }
  return dimensions
    .map((dim, i) => dimensionToDependentEventFilter(dim, values[i], fallback, forSearch))
    .filter(isNonNull);
};

// DimensionToDependentEventFilter converts a dimension and value pair to a valid DependentEventFilter
// mirrors logic in fs/api/metrics/shared/util.go
export function dimensionToDependentEventFilter(
  dimension: BareDimension,
  value: string,
  fallback?: boolean,
  forSearch?: boolean,
): segment.DependentEventFilter | null {
  if (
    dimension.customEventProperty ||
    dimension.userVarProperty ||
    dimension.pageProperty ||
    dimension.elementProperty
  ) {
    let dynamicField: CustomField;
    if (dimension.customEventProperty) {
      dynamicField = parseUnscopedFieldName(dimension.customEventProperty, FieldScope.Event);
    } else if (dimension.userVarProperty) {
      dynamicField = parseUnscopedFieldName(dimension.userVarProperty, FieldScope.User);
    } else if (dimension.pageProperty) {
      dynamicField = parseUnscopedFieldName(dimension.pageProperty, FieldScope.Page);
    } else {
      dynamicField = parseUnscopedFieldName(dimension.elementProperty, FieldScope.Element);
    }
    return dynamicFieldToDependentEventFilter(dynamicField, value);
  } else if (dimension.builtInProperty) {
    const val = dimension.prefix + value;
    switch (dimension.builtInProperty) {
      case metrics.BuiltInDimension.DIMENSION_CITY:
        return {
          city: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_STATE:
        return {
          region: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_COUNTRY:
        return {
          country: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_IP_ADDRESS:
        return {
          ipAddress: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_USER_ID:
        return {
          userId: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_EMAIL:
        return {
          email: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_NAME:
        return {
          name: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_USER_AGENT:
        return {
          userAgent: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_APP_VIEW_NAME:
        return {
          appViewName: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_APP_PACKAGE_NAME:
        return {
          appPackageName: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_APP_NAME:
        return {
          appName: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_DEVICE_MODEL:
        return {
          deviceModel: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_DEVICE_VENDOR:
        return {
          deviceVendor: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_BROWSER: {
        const browser: segment.Browser = toBrowserValue(val);
        if (browser == null) return null;
        return {
          browser: {
            equals: {
              value: [browser],
            },
          },
        };
      }
      case metrics.BuiltInDimension.DIMENSION_BROWSER_VERSION:
        return {
          browserVersion: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_DEVICE: {
        const device: segment.Device = toDeviceValue(val);
        if (device == null) return null;
        return {
          device: {
            equals: {
              value: [device],
            },
          },
        };
      }
      case metrics.BuiltInDimension.DIMENSION_PLATFORM: {
        const platform: segment.Platform = toPlatformValue(val);
        if (platform == null) return null;
        return {
          platform: {
            equals: {
              value: [platform],
            },
          },
        };
      }
      case metrics.BuiltInDimension.DIMENSION_OS: {
        const os: segment.OS = toOsValue(val);
        if (os == null) return null;
        return {
          os: {
            equals: {
              value: [os],
            },
          },
        };
      }
      case metrics.BuiltInDimension.DIMENSION_APP_VERSION:
        return {
          appVersion: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_APP_OS_VERSION:
        return {
          appOsVersion: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_APP_FS_VERSION:
        return {
          appFsVersion: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_URL:
        return {
          url: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FULL,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_URL_HOST:
        return {
          url: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_HOST,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_URL_FRAGMENT:
        return {
          url: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FRAGMENT,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_URL_QUERY:
        return {
          url: {
            containsToken: {
              value: [
                {
                  category: forSearch
                    ? segment.UrlCategory.URL_QUERY
                    : segment.UrlCategory.URL_STANDARD_QUERY,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_URL_HASH_QUERY:
        return {
          url: {
            containsToken: {
              value: [
                {
                  category: forSearch
                    ? segment.UrlCategory.URL_QUERY
                    : segment.UrlCategory.URL_HASH_QUERY,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_URL_PATH:
        return {
          url: {
            equals: {
              value: [
                {
                  category: forSearch
                    ? segment.UrlCategory.URL_PATH
                    : segment.UrlCategory.URL_STANDARD_PATH,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_URL_HASH_PATH:
        return {
          url: {
            equals: {
              value: [
                {
                  category: forSearch
                    ? segment.UrlCategory.URL_PATH
                    : segment.UrlCategory.URL_HASH_PATH,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_REFERRER_URL:
        return {
          referrerUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FULL,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_REFERRER_HOST:
        return {
          referrerUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_HOST,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_REFERRER_FRAGMENT:
        return {
          referrerUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FRAGMENT,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_REFERRER_QUERY:
        return {
          referrerUrl: {
            containsToken: {
              value: [
                {
                  category: segment.UrlCategory.URL_QUERY,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_REFERRER_PATH:
        return {
          referrerUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_PATH,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_TEXT:
        return {
          text: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_CSS:
        if (fallback && forSearch) {
          return {
            css: {
              matches: {
                value: [val],
              },
            },
          };
        } else if (fallback) {
          return {
            css: {
              exact: {
                value: [val],
              },
            },
          };
        } else if (forSearch) {
          return {
            element: {
              withElementId: {
                value: [val],
                spec: [],
              },
            },
          };
        } else {
          return {
            element: {
              withHistoricalElementId: {
                value: [val],
                spec: [],
              },
            },
          };
        }
      case metrics.BuiltInDimension.DIMENSION_AJAX_REQUEST_METHOD:
        return {
          requestMethod: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_AJAX_REQUEST_STATUS:
        return {
          statusCode: {
            equals: {
              value: [val as int64],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_AJAX_REQUEST_URL:
        return {
          ajaxRequestUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FULL,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_HOST:
        return {
          ajaxRequestUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_HOST,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_FRAGMENT:
        return {
          ajaxRequestUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FRAGMENT,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_QUERY:
        return {
          ajaxRequestUrl: {
            containsToken: {
              value: [
                {
                  category: segment.UrlCategory.URL_QUERY,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_PATH:
        return {
          ajaxRequestUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_PATH,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL:
        return {
          webSourceFileUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FULL,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_HOST:
        return {
          webSourceFileUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_HOST,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_FRAGMENT:
        return {
          webSourceFileUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_FRAGMENT,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_QUERY:
        return {
          webSourceFileUrl: {
            containsToken: {
              value: [
                {
                  category: segment.UrlCategory.URL_QUERY,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_PATH:
        return {
          webSourceFileUrl: {
            equals: {
              value: [
                {
                  category: segment.UrlCategory.URL_PATH,
                  value: val,
                },
              ],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_MOBILE_SOURCE_FILE:
        return {
          mobileSourceFile: {
            equals: {
              value: [val],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_ERROR_CLICK_TYPE:
        const errorType: segment.ErrorClickType = toErrorClickTypeValue(val);
        if (errorType == null) return null;
        return {
          errorClickType: {
            equals: {
              value: [errorType],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_PAGE_DEF_ID:
        return {
          pageDefId: {
            equals: {
              value: [val as int64],
              spec: [],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_SCREEN_WIDTH:
        return {
          displayWidth: {
            equals: {
              value: [val as int64],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_SCREEN_HEIGHT:
        return {
          displayHeight: {
            equals: {
              value: [val as int64],
            },
          },
        };
      case metrics.BuiltInDimension.DIMENSION_API_EVENT_NAME:
        return {
          apiEventName: {
            named: {
              name: val,
            },
          },
        };
      // These are not used by the client, only dataloss API
      case metrics.BuiltInDimension.DIMENSION_DATALOSS_TYPE:
      case metrics.BuiltInDimension.DIMENSION_DATALOSS_ORIGIN:
        return null;
      default:
        return assertExhaustive(dimension.builtInProperty);
    }
  } else {
    // unsupported dimension field
    return null;
  }
}

interface dynamicFieldFilterFactory {
  str(op: CustomFieldStringOperator): segment.DependentEventFilter;
  strs(op: CustomFieldStringsOperator): segment.DependentEventFilter;
  bool(op: CustomFieldBoolOperator): segment.DependentEventFilter;
  bools(op: CustomFieldBoolsOperator): segment.DependentEventFilter;
  int(op: CustomFieldIntOperator): segment.DependentEventFilter;
  ints(op: CustomFieldIntsOperator): segment.DependentEventFilter;
  float(op: CustomFieldFloatOperator): segment.DependentEventFilter;
  floats(op: CustomFieldFloatsOperator): segment.DependentEventFilter;
  date(op: CustomFieldDateOperator): segment.DependentEventFilter;
  dates(op: CustomFieldDatesOperator): segment.DependentEventFilter;
}

const dynamicFieldFilterFactories = new Map<FieldScope, dynamicFieldFilterFactory>([
  [
    FieldScope.User,
    {
      str(op: CustomFieldStringOperator): segment.DependentEventFilter {
        return { userVarString: op };
      },
      strs(op: CustomFieldStringsOperator): segment.DependentEventFilter {
        return { userVarStrings: op };
      },
      bool(op: CustomFieldBoolOperator): segment.DependentEventFilter {
        return { userVarBool: op };
      },
      bools(op: CustomFieldBoolsOperator): segment.DependentEventFilter {
        return { userVarBools: op };
      },
      int(op: CustomFieldIntOperator): segment.DependentEventFilter {
        return { userVarInt: op };
      },
      ints(op: CustomFieldIntsOperator): segment.DependentEventFilter {
        return { userVarInts: op };
      },
      float(op: CustomFieldFloatOperator): segment.DependentEventFilter {
        return { userVarFloat: op };
      },
      floats(op: CustomFieldFloatsOperator): segment.DependentEventFilter {
        return { userVarFloats: op };
      },
      date(op: CustomFieldDateOperator): segment.DependentEventFilter {
        return { userVarDate: op };
      },
      dates(op: CustomFieldDatesOperator): segment.DependentEventFilter {
        return { userVarDates: op };
      },
    },
  ],
  [
    FieldScope.Event,
    {
      str(op: CustomFieldStringOperator): segment.DependentEventFilter {
        return { customEventPropertyString: op };
      },
      strs(op: CustomFieldStringsOperator): segment.DependentEventFilter {
        return { customEventPropertyStrings: op };
      },
      bool(op: CustomFieldBoolOperator): segment.DependentEventFilter {
        return { customEventPropertyBool: op };
      },
      bools(op: CustomFieldBoolsOperator): segment.DependentEventFilter {
        return { customEventPropertyBools: op };
      },
      int(op: CustomFieldIntOperator): segment.DependentEventFilter {
        return { customEventPropertyInt: op };
      },
      ints(op: CustomFieldIntsOperator): segment.DependentEventFilter {
        return { customEventPropertyInts: op };
      },
      float(op: CustomFieldFloatOperator): segment.DependentEventFilter {
        return { customEventPropertyFloat: op };
      },
      floats(op: CustomFieldFloatsOperator): segment.DependentEventFilter {
        return { customEventPropertyFloats: op };
      },
      date(op: CustomFieldDateOperator): segment.DependentEventFilter {
        return { customEventPropertyDate: op };
      },
      dates(op: CustomFieldDatesOperator): segment.DependentEventFilter {
        return { customEventPropertyDates: op };
      },
    },
  ],
  [
    FieldScope.Page,
    {
      str(op: CustomFieldStringOperator): segment.DependentEventFilter {
        return { pageVarString: op };
      },
      strs(op: CustomFieldStringsOperator): segment.DependentEventFilter {
        return { pageVarStrings: op };
      },
      bool(op: CustomFieldBoolOperator): segment.DependentEventFilter {
        return { pageVarBool: op };
      },
      bools(op: CustomFieldBoolsOperator): segment.DependentEventFilter {
        return { pageVarBools: op };
      },
      int(op: CustomFieldIntOperator): segment.DependentEventFilter {
        return { pageVarInt: op };
      },
      ints(op: CustomFieldIntsOperator): segment.DependentEventFilter {
        return { pageVarInts: op };
      },
      float(op: CustomFieldFloatOperator): segment.DependentEventFilter {
        return { pageVarFloat: op };
      },
      floats(op: CustomFieldFloatsOperator): segment.DependentEventFilter {
        return { pageVarFloats: op };
      },
      date(op: CustomFieldDateOperator): segment.DependentEventFilter {
        return { pageVarDate: op };
      },
      dates(op: CustomFieldDatesOperator): segment.DependentEventFilter {
        return { pageVarDates: op };
      },
    },
  ],
  [
    FieldScope.Element,
    {
      str(op: CustomFieldStringOperator): segment.DependentEventFilter {
        return { elemVarString: op };
      },
      strs(op: CustomFieldStringsOperator): segment.DependentEventFilter {
        return { elemVarStrings: op };
      },
      bool(op: CustomFieldBoolOperator): segment.DependentEventFilter {
        return { elemVarBool: op };
      },
      bools(op: CustomFieldBoolsOperator): segment.DependentEventFilter {
        return { elemVarBools: op };
      },
      int(op: CustomFieldIntOperator): segment.DependentEventFilter {
        return { elemVarInt: op };
      },
      ints(op: CustomFieldIntsOperator): segment.DependentEventFilter {
        return { elemVarInts: op };
      },
      float(op: CustomFieldFloatOperator): segment.DependentEventFilter {
        return { elemVarFloat: op };
      },
      floats(op: CustomFieldFloatsOperator): segment.DependentEventFilter {
        return { elemVarFloats: op };
      },
      date(op: CustomFieldDateOperator): segment.DependentEventFilter {
        return { elemVarDate: op };
      },
      dates(op: CustomFieldDatesOperator): segment.DependentEventFilter {
        return { elemVarDates: op };
      },
    },
  ],
]);

export function dynamicFieldToDependentEventFilter(
  field: CustomField,
  value: string,
): segment.DependentEventFilter | null {
  const factories = dynamicFieldFilterFactories.get(field.scope);
  if (!factories) {
    return null; // scope not implemented
  }

  switch (field.varType) {
    case VarType.Str:
      return factories.str({
        equals: {
          name: field.ident,
          value: [value],
        },
      });
    case VarType.Strs:
      return factories.strs({
        equals: {
          name: field.ident,
          value: [value],
        },
      });
    case VarType.Bool:
      return factories.bool({
        equals: {
          name: field.ident,
          value: value === 'true',
        },
      });
    case VarType.Bools:
      return factories.bools({
        equals: {
          name: field.ident,
          value: value === 'true',
        },
      });
    case VarType.Int:
      return factories.int({
        equals: {
          name: field.ident,
          value: [value as int64],
        },
      });
    case VarType.Ints:
      return factories.ints({
        equals: {
          name: field.ident,
          value: [value as int64],
        },
      });
    case VarType.Real:
      return factories.float({
        equals: {
          name: field.ident,
          value: [parseFloat(value)],
        },
      });
    case VarType.Reals:
      return factories.floats({
        equals: {
          name: field.ident,
          value: [parseFloat(value)],
        },
      });
    case VarType.Date: {
      // Fake equality by issuing a range query for a single millisecond.
      const start = new Date(value);
      const end = new Date(start);
      end.setMilliseconds(end.getMilliseconds() + 1);
      return factories.date({
        range: {
          name: field.ident,
          value: {
            startTime: start.toISOString(),
            endTime: end.toISOString(),
          },
        },
      });
    }
    case VarType.Dates: {
      // Fake equality by issuing a range query for a single millisecond.
      const start = new Date(value);
      const end = new Date(start);
      end.setMilliseconds(end.getMilliseconds() + 1);
      return factories.dates({
        range: {
          name: field.ident,
          value: {
            startTime: start.toISOString(),
            endTime: end.toISOString(),
          },
        },
      });
    }
    default:
      return assertExhaustive(field.varType);
  }
}

function toBrowserValue(browser: string): segment.Browser {
  const browserEnum: schema.BrowserEnum = browser as schema.BrowserEnum;
  if (!Object.values(schema.BrowserEnum).includes(browserEnum)) {
    return null;
  }

  switch (browserEnum) {
    case schema.BrowserEnum.BrowserCocCoc:
      return segment.Browser.BROWSER_COCCOC;
    case schema.BrowserEnum.BrowserChrome:
      return segment.Browser.BROWSER_CHROME;
    case schema.BrowserEnum.BrowserCustomAgent:
      return segment.Browser.BROWSER_CUSTOMAGENT;
    case schema.BrowserEnum.BrowserCustomMobileAgent:
      return segment.Browser.BROWSER_CUSTOMMOBILEAGENT;
    case schema.BrowserEnum.BrowserDuckDuckGo:
      return segment.Browser.BROWSER_DUCKDUCKGO;
    case schema.BrowserEnum.BrowserElectron:
      return segment.Browser.BROWSER_ELECTRON;
    case schema.BrowserEnum.BrowserFirefox:
      return segment.Browser.BROWSER_FIREFOX;
    case schema.BrowserEnum.BrowserInternetExplorer:
      return segment.Browser.BROWSER_IE;
    case schema.BrowserEnum.BrowserLynx:
      return segment.Browser.BROWSER_LYNX;
    case schema.BrowserEnum.BrowserMicrosoftEdge:
      return segment.Browser.BROWSER_EDGE;
    case schema.BrowserEnum.BrowserMobileApp:
      return segment.Browser.BROWSER_MOBILEAPP;
    case schema.BrowserEnum.BrowserMozillaOther:
      return segment.Browser.BROWSER_MOZILLAOTHER;
    case schema.BrowserEnum.BrowserOpera:
      return segment.Browser.BROWSER_OPERA;
    case schema.BrowserEnum.BrowserRobot:
      return segment.Browser.BROWSER_ROBOT;
    case schema.BrowserEnum.BrowserSafari:
      return segment.Browser.BROWSER_SAFARI;
    case schema.BrowserEnum.BrowserSeaMonkey:
      return segment.Browser.BROWSER_SEAMONKEY;
    case schema.BrowserEnum.BrowserUCWeb:
      return segment.Browser.BROWSER_UCWEB;
    case schema.BrowserEnum.BrowserUnknown:
      return segment.Browser.BROWSER_UNKNOWN;
    case schema.BrowserEnum.BrowserYandex:
      return segment.Browser.BROWSER_YANDEX;
    default:
      assertExhaustive(browserEnum);
  }
}

export function toDeviceValue(device: string): segment.Device {
  const deviceEnum: schema.DeviceEnum = device as schema.DeviceEnum;
  if (!Object.values(schema.DeviceEnum).includes(deviceEnum)) {
    return null;
  }

  switch (deviceEnum) {
    case schema.DeviceEnum.DeviceDesktop:
      return segment.Device.DEVICE_DESKTOP;
    case schema.DeviceEnum.DeviceMobile:
      return segment.Device.DEVICE_MOBILE;
    case schema.DeviceEnum.DeviceTablet:
      return segment.Device.DEVICE_TABLET;
    case schema.DeviceEnum.DeviceRobot:
      return segment.Device.DEVICE_ROBOT;
    case schema.DeviceEnum.DeviceUnknown:
      return segment.Device.DEVICE_UNKNOWN;
    default:
      return assertExhaustive(deviceEnum);
  }
}

function toPlatformValue(platform: string): segment.Platform | null {
  const platformEnum: schema.PlatformEnum = platform as schema.PlatformEnum;
  if (!Object.values(schema.PlatformEnum).includes(platformEnum)) {
    return null;
  }

  switch (platformEnum) {
    case schema.PlatformEnum.PlatformNativeMobile:
      return segment.Platform.PLATFORM_NATIVEMOBILE;
    case schema.PlatformEnum.PlatformWeb:
      return segment.Platform.PLATFORM_WEB;
    case schema.PlatformEnum.PlatformServer:
      return segment.Platform.PLATFORM_SERVER;
    default:
      return assertExhaustive(platformEnum);
  }
}

function toOsValue(os: string): segment.OS | null {
  const osEnum: schema.OSEnum = os as schema.OSEnum;
  if (!Object.values(schema.OSEnum).includes(osEnum)) {
    return null;
  }

  switch (osEnum) {
    case schema.OSEnum.OSAndroid:
      return segment.OS.OS_ANDROID;
    case schema.OSEnum.OSChromeOS:
      return segment.OS.OS_CHROME;
    case schema.OSEnum.OSLinux:
      return segment.OS.OS_LINUX;
    case schema.OSEnum.OSOSX:
      return segment.OS.OS_OSX;
    case schema.OSEnum.OSRobot:
      return segment.OS.OS_ROBOT;
    case schema.OSEnum.OSUnknown:
      return segment.OS.OS_UNKNOWN;
    case schema.OSEnum.OSWindows:
      return segment.OS.OS_WINDOWS;
    case schema.OSEnum.OSWindowsPhone:
      return segment.OS.OS_WINDOWSPHONE;
    case schema.OSEnum.OSiOS:
      return segment.OS.OS_IOS;
    default:
      return assertExhaustive(osEnum);
  }
}

function toErrorClickTypeValue(ect: string): segment.ErrorClickType | null {
  const errorClickCode: schema.ErrorClickCode = Number(ect) as schema.ErrorClickCode;
  if (!Object.values(schema.ErrorClickCode).includes(errorClickCode)) {
    return null;
  }

  switch (errorClickCode) {
    case schema.ErrorClickCode.ERROR_CLICK_CODE_INVALID:
      return segment.ErrorClickType.ERROR_CLICK_TYPE_INVALID;
    case schema.ErrorClickCode.ERROR_CLICK_CODE_UNKNOWN:
      return segment.ErrorClickType.ERROR_CLICK_TYPE_UNKNOWN;
    case schema.ErrorClickCode.ERROR_CLICK_CODE_CONSOLE:
      return segment.ErrorClickType.ERROR_CLICK_TYPE_CONSOLE;
    case schema.ErrorClickCode.ERROR_CLICK_CODE_EXCEPTION:
      return segment.ErrorClickType.ERROR_CLICK_TYPE_EXCEPTION;
    default:
      return assertExhaustive(errorClickCode);
  }
}
